import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import routes from '../seo/routes';

const { langFromPath, mirrorPath } = routes;

const LanguageContext = createContext({ lang: 'en', toggleLang: () => {} });

/**
 * The language is derived from the URL, never from component state: English
 * lives at "/" and "/moodle-ai-agent", Spanish at "/es" and "/es/agente-ia-moodle".
 * That is what makes the Spanish content crawlable, linkable and indexable.
 *
 * The public API is unchanged (`{ lang, toggleLang }`), so consumers do not care:
 * toggling now navigates to the mirrored URL instead of flipping state.
 */
export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const history = useHistory();

  const lang = langFromPath(location.pathname);

  const toggleLang = useCallback(() => {
    const nextLang = lang === 'en' ? 'es' : 'en';
    history.push({
      // The mirrored URL uses the OTHER language's slug, not the current one:
      // /moodle-ai-agent <-> /es/agente-ia-moodle.
      pathname: mirrorPath(location.pathname, nextLang),
      // Preserve whatever the visitor was looking at.
      search: location.search,
      hash: location.hash,
    });
  }, [lang, history, location.pathname, location.search, location.hash]);

  // Keep <html lang> in sync even on pages that do not render <Seo>.
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', lang);
    }
  }, [lang]);

  const value = useMemo(() => ({ lang, toggleLang }), [lang, toggleLang]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);
